import { THEME_META } from '../data/themes';
import { lsGet } from './storage';

const CEFR_ORDER = ['A1', 'A2', 'B1', 'B2', 'C1'];

// Theme IDs most associated with each learning track.
// Era-based tracks (chosen in TrackSelect) and score-based learning tracks
// (assigned by assignTrack) are both keyed here so getRecommendedThemesForUser
// can promote the right themes to the top of Home and All Paths.
// null = no theme priority → falls back to CEFR-ordered default.
const TRACK_THEMES = {
  // ── era preference (TrackSelect screen) ──────────────────────────────────
  'Pre-Islamic': ['muallaqat', 'fakhr', 'ritha', 'wasf'],
  'Abbasid':     ['hikma', 'wasf', 'madih', 'zuhd'],
  'Sufi':        ['sufi', 'zuhd', 'hikma'],

  // ── score-based learning tracks (assigned by assignTrack) ─────────────────
  // Foundations: most accessible themes — short nature verses, love poetry
  'Foundations': ['wasf', 'ghazal'],
  // Fluency: accessible + one rung up — adds wisdom & elegy
  'Fluency':     ['ghazal', 'wasf', 'hikma', 'ritha', 'karam'],
  // Classical: the established pre-Islamic and Abbasid canon
  'Classical':   ['hikma', 'ritha', 'fakhr', 'madih', 'karam', 'sufi'],
  // Modernist: thematic / analytical angle on familiar Abbasid texts
  'Modernist':   ['ghazal', 'hikma', 'madih', 'karam', 'wasf'],
  // Mixed Path: no theme priority — CEFR order only (null → fallback)
  'Mixed Path':  null,
  // Scholars: dense, complex texts — satire, asceticism, mysticism
  'Scholars':    ['hija', 'zuhd', 'sufi', 'fakhr', 'madih'],
};

// Difficulty groups shown per user level, in priority order
const LEVEL_DIFFICULTY_ORDER = {
  A1: ['A2', 'B1'],
  A2: ['A2', 'B1'],
  B1: ['B1', 'B2', 'A2'],
  B2: ['B2', 'B1', 'C1'],
  C1: ['C1', 'B2', 'B1', 'A2'],
};

// Reads the authoritative CEFR level — checks new key first, falls back to old enum mapping
export function readUserLevel() {
  const cefr = lsGet('diwanUserLevel');
  if (cefr && CEFR_ORDER.includes(cefr)) return cefr;

  const raw = (lsGet('diwanArabicLevel') || '').toUpperCase();
  if (raw.includes('UPPER_INTERMEDIATE')) return 'B2';
  if (raw.includes('INTERMEDIATE'))       return 'B1';
  if (raw.includes('ELEMENTARY'))         return 'A2';
  if (raw.includes('BEGINNER'))           return 'A2';
  if (raw.includes('ADVANCED'))           return 'C1';
  return 'B1'; // safe default — B1 is mid-range
}

// Returns all theme IDs ordered by relevance for the given CEFR level.
// Random Discovery and Feel Adventurous are NOT included — rendered separately.
export function getRecommendedThemes(userLevel) {
  const diffOrder = LEVEL_DIFFICULTY_ORDER[userLevel] ?? LEVEL_DIFFICULTY_ORDER['B1'];
  const allIds    = Object.keys(THEME_META);

  const ordered = diffOrder.flatMap(diff =>
    allIds.filter(id => THEME_META[id].level === diff)
  );

  // Append any themes not covered by the priority list (edge case future-proofing)
  const covered = new Set(ordered);
  const rest     = allIds.filter(id => !covered.has(id));

  return [...ordered, ...rest];
}

// "Surprise me" — picks a theme above the user's current level when possible,
// falls back to any theme when the user is at the top level or has no known level.
export function pickSurpriseTheme(userLevel) {
  const allIds  = Object.keys(THEME_META);
  const myIndex = CEFR_ORDER.indexOf(userLevel);
  const harder  = allIds.filter(id => CEFR_ORDER.indexOf(THEME_META[id].level) > myIndex);
  const pool    = harder.length > 0 ? harder : allIds;
  return pool[Math.floor(Math.random() * pool.length)];
}

// Kept for any future internal use; not exposed in UI
export function pickAdventurousTheme(userLevel) { return pickSurpriseTheme(userLevel); }
export function pickRandomTheme() {
  const allIds = Object.keys(THEME_META);
  return allIds[Math.floor(Math.random() * allIds.length)];
}

// Reads the learningTrack the user chose during onboarding
export function readLearningTrack() {
  return lsGet('diwanLearningTrack') || '';
}

// Like getRecommendedThemes but promotes themes matching the user's learning track to the front.
// Track themes keep their relative CEFR ordering; remaining themes follow in CEFR order.
export function getRecommendedThemesForUser(userLevel, learningTrack) {
  const all = getRecommendedThemes(userLevel);
  const trackIds = TRACK_THEMES[learningTrack];
  if (!trackIds) return all; // 'Surprise' or unset → default CEFR order
  const inTrack  = all.filter(id =>  trackIds.includes(id));
  const notTrack = all.filter(id => !trackIds.includes(id));
  return [...inTrack, ...notTrack];
}
